import '../styles/GlobalDesign.css';
import React, { useState, useEffect } from 'react';
import AdminLayout from '../components/AdminLayout';

const API_URL = process.env.NODE_ENV === 'production' ? '/api' : (process.env.REACT_APP_API_URL || '/api');
const XTREAM_URL = process.env.REACT_APP_XTREAM_URL || 'https://fluxivo.de';
const XTREAM_APP_NAME = 'Fluxivo';

const formatXtreamCredentials = ({ username, password, serverUrl = XTREAM_URL, m3uUrl, expiresAt }) => [
  `Name: ${XTREAM_APP_NAME}`,
  `Utilisateur: ${username}`,
  `Mot de passe: ${password}`,
  `URL Xtream: ${serverUrl}`,
  `M3U: ${m3uUrl}`,
  `⏰ Expire: ${new Date(expiresAt).toLocaleString('fr-FR')}`
].join('\n');

const EXTEND_PRESETS = [
  { label: '1 mois', days: 30 },
  { label: '3 mois', days: 90 },
  { label: '6 mois', days: 180 },
  { label: '1 an', days: 365 },
];

const CONVERT_PRESETS = [
  { label: '1 mois', days: 30 },
  { label: '3 mois', days: 90 },
  { label: '6 mois', days: 180 },
  { label: '1 an', days: 365 },
];

function AdminXtream() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // all | active | expired | trial | disabled
  const [selectedIds, setSelectedIds] = useState([]);
  const [copiedItem, setCopiedItem] = useState(null);
  const [testWhatsapp, setTestWhatsapp] = useState('');
  const [duration, setDuration] = useState(24);
  const [durationType, setDurationType] = useState('hours');
  const [generating, setGenerating] = useState(false);
  const [bulkCount, setBulkCount] = useState(1);
  const [bulkWhatsapp, setBulkWhatsapp] = useState('');
  const [bulkGenerating, setBulkGenerating] = useState(false);
  const [bulkResults, setBulkResults] = useState([]);

  // Modals
  const [extendModal, setExtendModal] = useState(null);
  const [extendDays, setExtendDays] = useState(30);
  const [extendAmount, setExtendAmount] = useState(2000);
  const [extendMode, setExtendMode] = useState('direct'); // 'direct' | 'link'
  const [generatedLink, setGeneratedLink] = useState('');
  const [convertModal, setConvertModal] = useState(null);
  const [convertDays, setConvertDays] = useState(30);
  const [actionLoading, setActionLoading] = useState(false);

  const [editConnModal, setEditConnModal] = useState(null);
  const [editConnValue, setEditConnValue] = useState(2);
  const [activeDevices, setActiveDevices] = useState([]);
  const [observedDevices, setObservedDevices] = useState([]);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [removingDeviceIp, setRemovingDeviceIp] = useState('');
  const [clearingAccountConnections, setClearingAccountConnections] = useState(false);

  // Paiements en attente
  const [pendingPayments, setPendingPayments] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [showPending, setShowPending] = useState(false);
  const [activatingToken, setActivatingToken] = useState(null);

  // Catégories bloquées
  const [blockCatModal, setBlockCatModal] = useState(null); // xtream account
  const [blockedCats, setBlockedCats] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [catSearch, setCatSearch] = useState('');
  const [blockLoading, setBlockLoading] = useState(false);

  // Réinitialisation globale des IPs
  const [resetAllLoading, setResetAllLoading] = useState(false);
  const [resetAllMsg, setResetAllMsg] = useState('');

  const token = () => localStorage.getItem('token');

  const authHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token()}`
  });

  const readApiResponse = async (res, fallback = 'Erreur serveur') => {
    const text = await res.text();
    let data = {};

    if (text) {
      try {
        data = JSON.parse(text);
      } catch {
        const status = res.status ? `HTTP ${res.status}` : 'réponse invalide';
        throw new Error(`${fallback} (${status}) : réponse serveur non JSON`);
      }
    } else if (!res.ok) {
      throw new Error(`${fallback} (HTTP ${res.status})`);
    }

    if (!res.ok || data.success === false) {
      throw new Error(data.error || data.message || `${fallback} (HTTP ${res.status})`);
    }

    return data;
  };

  const handleResetAllConnections = async () => {
    if (!window.confirm('Réinitialiser TOUTES les connexions actives ? Les clients devront recharger leur app mais ne perdront pas leur accès.')) return;
    setResetAllLoading(true);
    setResetAllMsg('');
    try {
      const res = await fetch(`${API_URL}/admin/connections/reset-all`, {
        method: 'DELETE',
        headers: authHeaders()
      });
      const data = await readApiResponse(res, 'Erreur réinitialisation');
      setResetAllMsg(data.message || 'Connexions réinitialisées');
      setTimeout(() => setResetAllMsg(''), 5000);
    } catch (e) {
      setResetAllMsg('Erreur : ' + e.message);
    } finally {
      setResetAllLoading(false);
    }
  };

  const formatCategoryKey = (cat) => `${cat.category_type || 'live'}:${cat.category_id}`;
  const getCategoryLabel = (cat) => {
    if (cat.category_type === 'vod') return `${cat.category_name} (Films)`;
    if (cat.category_type === 'series') return `${cat.category_name} (Séries)`;
    return cat.category_name;
  };

  const loadAccounts = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/admin/xtream-accounts`, {
        headers: { 'Authorization': `Bearer ${token()}` }
      });
      const data = await readApiResponse(res, 'Erreur chargement des comptes Xtream');
      setAccounts(data.accounts || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text, key) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        const el = document.createElement('textarea');
        el.value = text;
        el.style.position = 'fixed';
        el.style.left = '-9999px';
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
      }
      setCopiedItem(key);
      setTimeout(() => setCopiedItem(null), 2000);
    } catch {}
  };

  const calcDurationInDays = () => {
    if (durationType === 'hours') return duration / 24;
    if (durationType === 'days') return duration;
    if (durationType === 'months') return duration * 30;
    if (durationType === 'years') return duration * 365;
    return duration;
  };

  const formatDuration = () => {
    const map = { hours: 'heure(s)', days: 'jour(s)', months: 'mois', years: 'an(s)' };
    return `${duration} ${map[durationType]}`;
  };

  const formatBulkAccount = (account) => [
    `Name: ${XTREAM_APP_NAME}`,
    `Utilisateur: ${account.username}`,
    `Mot de passe: ${account.password}`,
    `URL: ${account.serverUrl || XTREAM_URL}`,
    `Lien de prolongement: ${account.renewalUrl}`
  ].join('\n');

  const formatBulkAccounts = (items) => items.map(formatBulkAccount).join('\n\n');

  const handleGenerateTest = async () => {
    setGenerating(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/admin/xtream-test/generate`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ duration: calcDurationInDays(), whatsapp: testWhatsapp || undefined })
      });
      const data = await readApiResponse(res, 'Erreur génération');
      await loadAccounts();
      const expiresAt = data.expires_at || data.expiresAt;
      const shortM3u = data.m3u_short || `${XTREAM_URL}/get.php?username=${data.username}&password=${data.password}&type=m3u_plus&output=ts`;
      const creds = formatXtreamCredentials({
        username: data.username,
        password: data.password,
        m3uUrl: shortM3u,
        expiresAt
      });
      await copyToClipboard(creds, 'new');
      alert(`✅ Compte test créé !\n\nName: ${XTREAM_APP_NAME}\n👤 ${data.username}\n🔑 ${data.password}\n🌐 ${XTREAM_URL}\n📋 M3U: ${shortM3u}\n⏰ Expire: ${new Date(expiresAt).toLocaleString('fr-FR')}`);
      setTestWhatsapp('');
    } catch (err) {
      setError(err.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleBulkGenerateTests = async () => {
    const count = Math.max(1, Math.min(500, parseInt(bulkCount, 10) || 1));
    setBulkGenerating(true);
    setError('');
    setBulkResults([]);
    try {
      const res = await fetch(`${API_URL}/admin/xtream-test/bulk-generate`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ count, duration: calcDurationInDays(), whatsapp: bulkWhatsapp || undefined })
      });
      const data = await readApiResponse(res, 'Erreur génération en lot');
      const items = data.accounts || [];
      setBulkResults(items);
      await loadAccounts();
      if (items.length > 0) {
        await copyToClipboard(formatBulkAccounts(items), 'bulk-all');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setBulkGenerating(false);
    }
  };

  const handleToggle = async (id) => {
    try {
      const res = await fetch(`${API_URL}/admin/xtream-accounts/${id}/toggle`, {
        method: 'PUT',
        headers: authHeaders()
      });
      await readApiResponse(res, 'Erreur activation/désactivation');
      await loadAccounts();
    } catch (err) { setError(err.message); }
  };

  const handleResetPassword = async (acc) => {
    if (!window.confirm(`Générer un nouveau mot de passe pour ${acc.nom || acc.username} ?\n\nL'ancien mot de passe sera inutilisable immédiatement.`)) return;
    try {
      const res = await fetch(`${API_URL}/admin/xtream-accounts/${acc.id}/reset-password`, {
        method: 'PUT',
        headers: authHeaders()
      });
      const data = await readApiResponse(res, 'Erreur génération mot de passe');
      await loadAccounts();
      window.alert(`✅ Nouveau mot de passe généré !\n\n👤 Utilisateur : ${data.username}\n🔑 Mot de passe : ${data.password}\n\nPensez à communiquer les nouveaux identifiants au client.`);
    } catch (err) { setError(err.message); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce compte Xtream ?')) return;
    try {
      const res = await fetch(`${API_URL}/admin/xtream-accounts/${id}`, {
        method: 'DELETE',
        headers: authHeaders()
      });
      await readApiResponse(res, 'Erreur suppression');
      setSelectedIds(prev => prev.filter(i => i !== id));
      await loadAccounts();
    } catch (err) { setError(err.message); }
  };

  const triggerWhatsAppClient = (acc) => {
    const rawPhone = (acc.whatsapp || '').trim();
    if (!rawPhone) {
      setError('Aucun numéro WhatsApp renseigné pour ce compte.');
      return;
    }

    const normalizedPhone = rawPhone.replace(/\D/g, '');
    if (!normalizedPhone || normalizedPhone === '0000000000') {
      setError('Le numéro WhatsApp enregistré est invalide ou vide.');
      return;
    }

    const userName = acc.nom || acc.username || 'client';
    const message = `Bonjour ${userName},\n\nVotre abonnement Fluxivo expire bientôt.\n\n📅 Pour continuer à profiter de tous nos contenus, renouvelez votre accès dès maintenant.\n\n👉 Renouveler mon abonnement: http://51.254.135.114/plans\n\nMerci de votre confiance ! 🙏`;
    const whatsappUrl = `https://wa.me/${normalizedPhone}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Supprimer ${selectedIds.length} compte(s) sélectionné(s) ?`)) return;
    try {
      await Promise.all(selectedIds.map(async (id) => {
        const res = await fetch(`${API_URL}/admin/xtream-accounts/${id}`, { method: 'DELETE', headers: authHeaders() });
        return readApiResponse(res, 'Erreur suppression');
      }));
      setSelectedIds([]);
      await loadAccounts();
    } catch (err) { setError(err.message); }
  };

  const toggleSelect = (id) => setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  const toggleSelectAll = (list) => setSelectedIds(prev => prev.length === list.length ? [] : list.map(a => a.id));

  const loadPendingPayments = async () => {
    setPendingLoading(true);
    try {
      const res = await fetch(`${API_URL}/admin/pending-payments`, { headers: authHeaders() });
      const data = await readApiResponse(res, 'Erreur chargement paiements');
      setPendingPayments(data.payments || []);
    } catch (err) { setError(err.message); }
    finally { setPendingLoading(false); }
  };

  const handleForceActivate = async (tok) => {
    if (!window.confirm('Activer manuellement ce paiement ?')) return;
    setActivatingToken(tok);
    try {
      const res = await fetch(`${API_URL}/admin/payments/${tok}/force-activate`, {
        method: 'POST',
        headers: authHeaders()
      });
      await readApiResponse(res, 'Échec activation');
      alert('✅ Paiement activé avec succès !');
      await loadPendingPayments();
      await loadAccounts();
    } catch (err) { alert('❌ ' + err.message); }
    finally { setActivatingToken(null); }
  };

  const handleSaveConnections = async () => {
    if (!editConnModal) return;
    try {
      const res = await fetch(`${API_URL}/admin/xtream-accounts/${editConnModal.id}/connections`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ max_connections: editConnValue })
      });
      await readApiResponse(res, 'Erreur mise à jour appareils');
      setEditConnModal(null);
      await loadAccounts();
    } catch (err) { setError(err.message); }
  };

  const loadActiveDevices = async (acc) => {
    if (!acc) return;
    setDevicesLoading(true);
    try {
      const res = await fetch(`${API_URL}/admin/xtream-accounts/${acc.id}/active-devices`, {
        headers: authHeaders()
      });
      const data = await readApiResponse(res, 'Erreur chargement appareils');
      setActiveDevices(data.devices || []);
      setObservedDevices(data.observed_devices || []);
    } catch (err) {
      setError(err.message);
      setActiveDevices([]);
      setObservedDevices([]);
    } finally {
      setDevicesLoading(false);
    }
  };

  const openConnectionsModal = async (acc) => {
    setEditConnModal(acc);
    setEditConnValue(acc.max_connections || 2);
    setActiveDevices([]);
    setObservedDevices([]);
    await loadActiveDevices(acc);
  };

  const handleRemoveActiveDevice = async (device) => {
    if (!editConnModal || !device) return;
    const deviceLabel = device.device_id || device.ip_address || `#${device.id}`;
    if (!window.confirm(`Supprimer l'appareil autorisé ${deviceLabel} ? Il devra reprendre une place à sa prochaine connexion.`)) return;
    const actionKey = `remove:${device.id || device.ip_address}`;
    setRemovingDeviceIp(actionKey);
    try {
      const endpoint = device.id
        ? `${API_URL}/admin/xtream-accounts/${editConnModal.id}/allowed-devices/${device.id}`
        : `${API_URL}/admin/xtream-accounts/${editConnModal.id}/active-devices/${encodeURIComponent(device.ip_address)}`;
      const res = await fetch(endpoint, {
        method: 'DELETE',
        headers: authHeaders()
      });
      await readApiResponse(res, 'Erreur suppression appareil');
      await loadActiveDevices(editConnModal);
      await loadAccounts();
    } catch (err) {
      setError(err.message);
    } finally {
      setRemovingDeviceIp('');
    }
  };

  const handleDisconnectDevice = async (device) => {
    if (!editConnModal || !device?.ip_address) return;
    const deviceLabel = device.device_id || device.ip_address;
    if (!window.confirm(`Déconnecter ${deviceLabel} ? L'appareil reste autorisé et pourra se reconnecter.`)) return;
    const actionKey = `disconnect:${device.ip_address}`;
    setRemovingDeviceIp(actionKey);
    try {
      const res = await fetch(`${API_URL}/admin/xtream-accounts/${editConnModal.id}/connections/${encodeURIComponent(device.ip_address)}`, {
        method: 'DELETE',
        headers: authHeaders()
      });
      await readApiResponse(res, 'Erreur suppression connexion');
      await loadActiveDevices(editConnModal);
      await loadAccounts();
    } catch (err) {
      setError(err.message);
    } finally {
      setRemovingDeviceIp('');
    }
  };

  const handleRemoveObservedDevice = async (device) => {
    if (!editConnModal || !device?.ip_address) return;
    if (!window.confirm(`Supprimer l'IP observée ${device.ip_address} ?`)) return;
    const actionKey = `observed:${device.ip_address}`;
    setRemovingDeviceIp(actionKey);
    try {
      const res = await fetch(`${API_URL}/admin/xtream-accounts/${editConnModal.id}/active-devices/${encodeURIComponent(device.ip_address)}`, {
        method: 'DELETE',
        headers: authHeaders()
      });
      await readApiResponse(res, 'Erreur suppression appareil observé');
      await loadActiveDevices(editConnModal);
      await loadAccounts();
    } catch (err) {
      setError(err.message);
    } finally {
      setRemovingDeviceIp('');
    }
  };

  const handleClearAccountConnections = async () => {
    if (!editConnModal) return;
    if (!window.confirm(`Vider toutes les connexions actives de ${editConnModal.username} ? Les appareils autorisés resteront enregistrés.`)) return;
    setClearingAccountConnections(true);
    try {
      const res = await fetch(`${API_URL}/admin/xtream-accounts/${editConnModal.id}/connections`, {
        method: 'DELETE',
        headers: authHeaders()
      });
      await readApiResponse(res, 'Erreur suppression connexions');
      await loadActiveDevices(editConnModal);
      await loadAccounts();
    } catch (err) {
      setError(err.message);
    } finally {
      setClearingAccountConnections(false);
    }
  };

  const handleExtend = async () => {
    if (!extendModal || extendDays === 0) return;
    setActionLoading(true);
    try {
      const res = await fetch(`${API_URL}/admin/xtream-accounts/${extendModal.id}/extend`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ days: extendDays })
      });
      await readApiResponse(res, 'Erreur prolongation');
      setExtendModal(null);
      setGeneratedLink('');
      await loadAccounts();
    } catch (err) { setError(err.message); }
    finally { setActionLoading(false); }
  };

  const handleGenerateLink = async () => {
    if (!extendModal || extendDays <= 0 || extendAmount <= 0) return;
    setActionLoading(true);
    setGeneratedLink('');
    try {
      const res = await fetch(`${API_URL}/admin/xtream-accounts/${extendModal.id}/payment-link`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ days: extendDays, amount: extendAmount })
      });
      const data = await readApiResponse(res, 'Erreur génération lien');
      setGeneratedLink(data.url);
    } catch (err) { setError(err.message); }
    finally { setActionLoading(false); }
  };

  const handleConvert = async () => {
    if (!convertModal || convertDays <= 0) return;
    setActionLoading(true);
    try {
      const res = await fetch(`${API_URL}/admin/xtream-accounts/${convertModal.id}/convert`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ days: convertDays })
      });
      await readApiResponse(res, 'Erreur conversion');
      setConvertModal(null);
      await loadAccounts();
    } catch (err) { setError(err.message); }
    finally { setActionLoading(false); }
  };

  useEffect(() => { loadAccounts(); }, []); // eslint-disable-line

  const openBlockCatModal = async (acc) => {
    setBlockCatModal(acc);
    setBlockLoading(true);
    setCatSearch('');
    try {
      const [blockedRes, catsRes] = await Promise.all([
        fetch(`${API_URL}/admin/xtream-accounts/${acc.id}/blocked-categories`, { headers: authHeaders() }),
        fetch(`${API_URL}/admin/channels-manager/categories`, { headers: authHeaders() })
      ]);
      const blockedData = await readApiResponse(blockedRes, 'Erreur chargement catégories bloquées');
      const catsData = await readApiResponse(catsRes, 'Erreur chargement catégories');
      setBlockedCats((blockedData.blocked || []).map(b => `${b.category_type || 'live'}:${b.category_id}`));
      setAllCategories((catsData.categories || []).filter(c => c.category_id && c.category_name && c.category_type));
    } catch (err) { setError(err.message); }
    finally { setBlockLoading(false); }
  };

  const toggleBlockCategory = async (acc, category) => {
    const categoryKey = formatCategoryKey(category);
    const isBlocked = blockedCats.includes(categoryKey);
    try {
      if (isBlocked) {
        const res = await fetch(`${API_URL}/admin/xtream-accounts/${acc.id}/block-category/${category.category_id}?category_type=${category.category_type}`, {
          method: 'DELETE', headers: authHeaders()
        });
        await readApiResponse(res, 'Erreur déblocage catégorie');
        setBlockedCats(prev => prev.filter(c => c !== categoryKey));
      } else {
        const res = await fetch(`${API_URL}/admin/xtream-accounts/${acc.id}/block-category`, {
          method: 'POST', headers: authHeaders(),
          body: JSON.stringify({ category_id: category.category_id, category_type: category.category_type })
        });
        await readApiResponse(res, 'Erreur blocage catégorie');
        setBlockedCats(prev => [...prev, categoryKey]);
      }
    } catch (err) { setError(err.message); }
  };

  const handleBlockAll = async (filtered) => {
    if (!blockCatModal) return;
    const toBlock = filtered.filter(cat => !blockedCats.includes(formatCategoryKey(cat)));
    try {
      await Promise.all(toBlock.map(async (cat) => {
        const res = await fetch(`${API_URL}/admin/xtream-accounts/${blockCatModal.id}/block-category`, {
          method: 'POST', headers: authHeaders(),
          body: JSON.stringify({ category_id: cat.category_id, category_type: cat.category_type })
        });
        return readApiResponse(res, 'Erreur blocage catégorie');
      }));
      setBlockedCats(prev => [...new Set([...prev, ...toBlock.map(c => formatCategoryKey(c))])]);
    } catch (err) { setError(err.message); }
  };

  const handleUnblockAll = async (filtered) => {
    if (!blockCatModal) return;
    const toUnblock = filtered.filter(cat => blockedCats.includes(formatCategoryKey(cat)));
    try {
      await Promise.all(toUnblock.map(async (cat) => {
        const res = await fetch(`${API_URL}/admin/xtream-accounts/${blockCatModal.id}/block-category/${cat.category_id}?category_type=${cat.category_type}`, {
          method: 'DELETE', headers: authHeaders()
        });
        return readApiResponse(res, 'Erreur déblocage catégorie');
      }));
      setBlockedCats(prev => prev.filter(c => !toUnblock.map(u => formatCategoryKey(u)).includes(c)));
    } catch (err) { setError(err.message); }
  };

  const activeCount = accounts.filter(a => !a.is_expired && a.is_enabled !== 0).length;
  const testCount = accounts.filter(a => a.is_trial === 1).length;
  const expiredCount = accounts.filter(a => a.is_expired).length;
  const filteredAccounts = accounts.filter(a => {
    const matchSearch =
      (a.username || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (a.nom || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (a.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (a.reseller_name || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchFilter =
      filterStatus === 'all' ? true :
      filterStatus === 'active' ? (!a.is_expired && a.is_enabled !== 0 && !a.is_trial) :
      filterStatus === 'expired' ? a.is_expired :
      filterStatus === 'trial' ? a.is_trial === 1 :
      filterStatus === 'disabled' ? a.is_enabled === 0 : true;
    return matchSearch && matchFilter;
  });

  return (
    <AdminLayout>
      <div style={styles.page}>

        {/* Header */}
        <div style={styles.pageHeader}>
          <div>
            <h1 style={styles.pageTitle}>Comptes Xtream Codes</h1>
            <p style={styles.pageSubtitle}>Gestion des accès IPTV via protocole Xtream</p>
          </div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
            {resetAllMsg && (
              <span style={{ fontSize: '0.82rem', color: resetAllMsg.startsWith('Erreur') ? '#F87171' : '#22C55E', fontWeight: '500' }}>
                {resetAllMsg}
              </span>
            )}
            <button
              style={{ ...styles.backBtn, background: '#EF444422', border: '1px solid #EF4444', color: '#F87171', opacity: resetAllLoading ? 0.6 : 1 }}
              onClick={handleResetAllConnections}
              disabled={resetAllLoading}
              title="Libère toutes les connexions actives en mémoire. Les appareils peuvent se reconnecter immédiatement."
            >
              {resetAllLoading ? '⏳ Réinitialisation...' : '🔄 Réinitialiser toutes les IPs'}
            </button>
            <button style={styles.backBtn} onClick={() => window.location.href = '/admin/home'}>
              ← Retour
            </button>
          </div>
        </div>

        {/* Stats */}
        <div style={styles.statsRow}>
          {[
            { label: 'Total', value: accounts.length, color: '#3B82F6', icon: '📊' },
            { label: 'Actifs', value: activeCount, color: '#22C55E', icon: '✅' },
            { label: 'Tests', value: testCount, color: '#F59E0B', icon: '🎁' },
            { label: 'Expirés', value: expiredCount, color: '#EF4444', icon: '❌' },
          ].map(s => (
            <div key={s.label} style={{ ...styles.statCard, borderTop: `3px solid ${s.color}` }}>
              <span style={styles.statIcon}>{s.icon}</span>
              <span style={{ ...styles.statValue, color: s.color }}>{s.value}</span>
              <span style={styles.statLabel}>{s.label}</span>
            </div>
          ))}
        </div>

        {/* Paiements bloqués */}
        <div style={styles.generateCard}>
          <div
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => { const next = !showPending; setShowPending(next); if (next && pendingPayments.length === 0) loadPendingPayments(); }}
          >
            <h3 style={{ ...styles.cardTitle, margin: 0, color: pendingPayments.length > 0 ? '#F59E0B' : undefined }}>
              ⚠️ Paiements en attente
              {pendingPayments.length > 0 && (
                <span style={{ marginLeft: '10px', background: '#F59E0B', color: '#000', borderRadius: '999px', padding: '2px 10px', fontSize: '0.78rem', fontWeight: '700' }}>
                  {pendingPayments.length}
                </span>
              )}
            </h3>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              {showPending && (
                <button
                  onClick={e => { e.stopPropagation(); loadPendingPayments(); }}
                  style={{ padding: '4px 12px', fontSize: '0.8rem', background: '#1E3A5F', border: '1px solid #2D5A8E', borderRadius: '6px', color: '#94A3B8', cursor: 'pointer' }}
                >
                  🔄 Actualiser
                </button>
              )}
              <span style={{ color: '#94A3B8', fontSize: '1.1rem' }}>{showPending ? '▲' : '▼'}</span>
            </div>
          </div>

          {showPending && (
            <div style={{ marginTop: '16px' }}>
              {pendingLoading ? (
                <div style={{ textAlign: 'center', color: '#94A3B8', padding: '20px' }}>⏳ Chargement...</div>
              ) : pendingPayments.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#22C55E', padding: '20px' }}>✅ Aucun paiement bloqué</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {pendingPayments.map(p => {
                    let info = {};
                    try { info = typeof p.personal_info === 'string' ? (JSON.parse(p.personal_info)[0] || JSON.parse(p.personal_info)) : (Array.isArray(p.personal_info) ? p.personal_info[0] : p.personal_info); } catch {}
                    const isActivating = activatingToken === p.token_pay;
                    return (
                      <div key={p.token_pay} style={{ background: '#12263A', border: '1px solid #2D3F50', borderRadius: '10px', padding: '14px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                            <span style={{ fontWeight: '700', color: '#E2E8F0' }}>{p.nom || p.email || `User #${p.user_id}`}</span>
                            {p.email && p.nom && <span style={{ color: '#64748B', fontSize: '0.82rem' }}>{p.email}</span>}
                            <span style={{ background: '#1E3A5F', border: '1px solid #2D5A8E', borderRadius: '6px', padding: '2px 8px', fontSize: '0.78rem', color: '#60A5FA' }}>
                              {info.planType || p.plan_type || '—'}
                            </span>
                            <span style={{ color: '#F59E0B', fontWeight: '600', fontSize: '0.88rem' }}>
                              {p.montant ? `${p.montant} FCFA` : ''}
                            </span>
                          </div>
                          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', fontSize: '0.78rem', color: '#64748B' }}>
                            <span>📅 {new Date(p.created_at).toLocaleString('fr-FR')}</span>
                            {info.generated_by_reseller && <span>🤝 Revendeur #{info.generated_by_reseller}</span>}
                            {info.whatsappNumber && <span>📱 {info.whatsappNumber}</span>}
                            <span style={{ fontFamily: 'monospace', fontSize: '0.72rem' }}>🔑 {p.token_pay?.substring(0, 16)}…</span>
                          </div>
                        </div>
                        <button
                          onClick={() => handleForceActivate(p.token_pay)}
                          disabled={isActivating}
                          style={{ padding: '8px 18px', background: isActivating ? '#1E3A5F' : '#16A34A22', border: `1px solid ${isActivating ? '#2D5A8E' : '#16A34A'}`, borderRadius: '8px', color: isActivating ? '#64748B' : '#4ADE80', cursor: isActivating ? 'not-allowed' : 'pointer', fontWeight: '700', fontSize: '0.88rem', whiteSpace: 'nowrap' }}
                        >
                          {isActivating ? '⏳ Activation...' : '⚡ Activer'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Génération */}
        <div style={styles.generateCard}>
          <h3 style={styles.cardTitle}>➕ Générer un compte test</h3>
          <div style={styles.generateForm}>
            <input
              style={styles.input}
              placeholder="Numéro WhatsApp optionnel (ex: +228 99 99 99 99)"
              value={testWhatsapp}
              onChange={e => setTestWhatsapp(e.target.value)}
            />
            <div style={styles.quickBtns}>
              {[
                { label: '1h', value: 1, type: 'hours' },
                { label: '3h', value: 3, type: 'hours' },
                { label: '24h', value: 24, type: 'hours' },
                { label: '2j', value: 2, type: 'days' },
                { label: '7j', value: 7, type: 'days' },
                { label: '30j', value: 30, type: 'days' },
              ].map(p => (
                <button
                  key={p.label}
                  style={{ ...styles.quickBtn, ...(duration === p.value && durationType === p.type ? styles.quickBtnActive : {}) }}
                  onClick={() => { setDuration(p.value); setDurationType(p.type); }}
                >{p.label}</button>
              ))}
            </div>
            <div style={styles.customDuration}>
              <input
                type="number" min="1"
                style={{ ...styles.input, width: '80px' }}
                value={duration}
                onChange={e => setDuration(parseInt(e.target.value) || 1)}
              />
              <select style={styles.select} value={durationType} onChange={e => setDurationType(e.target.value)}>
                <option value="hours">Heure(s)</option>
                <option value="days">Jour(s)</option>
                <option value="months">Mois</option>
                <option value="years">An(s)</option>
              </select>
              <span style={styles.durationLabel}>→ <strong>{formatDuration()}</strong></span>
            </div>
            <button style={styles.generateBtn} onClick={handleGenerateTest} disabled={generating}>
              {generating ? '⏳ Génération...' : `🎯 Générer (${formatDuration()})`}
            </button>
          </div>
        </div>

        <div style={styles.generateCard}>
          <h3 style={styles.cardTitle}>⚡ Générer plusieurs comptes test</h3>
          <div style={styles.generateForm}>
            <input
              style={styles.input}
              placeholder="WhatsApp optionnel pour le premier compte généré"
              value={bulkWhatsapp}
              onChange={e => setBulkWhatsapp(e.target.value)}
            />
            <div style={styles.bulkRow}>
              <div style={{ flex: '0 0 160px' }}>
                <label style={styles.fieldLabel}>Nombre</label>
                <input
                  type="number"
                  min="1"
                  max="500"
                  style={styles.input}
                  value={bulkCount}
                  onChange={e => setBulkCount(Math.max(1, Math.min(500, parseInt(e.target.value, 10) || 1)))}
                />
              </div>
              <button style={styles.generateBtn} onClick={handleBulkGenerateTests} disabled={bulkGenerating}>
                {bulkGenerating ? '⏳ Génération...' : `⚡ Générer ${Math.max(1, Math.min(500, parseInt(bulkCount, 10) || 1))} tests (${formatDuration()})`}
              </button>
            </div>

            {bulkResults.length > 0 && (
              <div style={styles.bulkResultBox}>
                <div style={styles.bulkResultHeader}>
                  <span style={styles.bulkResultTitle}>✅ {bulkResults.length} compte(s) généré(s)</span>
                  <button
                    style={{ ...styles.copyLinkBtn, ...(copiedItem === 'bulk-all' ? styles.copyBtnDone : {}) }}
                    onClick={() => copyToClipboard(formatBulkAccounts(bulkResults), 'bulk-all')}
                  >
                    {copiedItem === 'bulk-all' ? '✅ Copié' : '📋 Copier tous'}
                  </button>
                </div>
                <div style={styles.bulkList}>
                  {bulkResults.slice(0, 50).map((account) => (
                    <div key={account.username} style={styles.bulkItem}>
                      <div style={styles.bulkCreds}>
                        <strong>Name:</strong> {XTREAM_APP_NAME}<br />
                        <strong>Utilisateur:</strong> {account.username}<br />
                        <strong>Mot de passe:</strong> {account.password}<br />
                        <strong>URL:</strong> {account.serverUrl || XTREAM_URL}<br />
                        <strong>Lien:</strong> <span style={styles.bulkLink}>{account.renewalUrl}</span>
                      </div>
                      <button
                        style={styles.copyBtn}
                        onClick={() => copyToClipboard(formatBulkAccount(account), `bulk-${account.username}`)}
                      >
                        {copiedItem === `bulk-${account.username}` ? '✅' : '📋'}
                      </button>
                    </div>
                  ))}
                  {bulkResults.length > 50 && (
                    <div style={styles.bulkMore}>+ {bulkResults.length - 50} autre(s) compte(s) dans la copie complète</div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {error && <div style={styles.errorBanner}>❌ {error}</div>}

        {/* Recherche + Filtres + Suppression en bloc */}
        {!loading && accounts.length > 0 && (
          <div style={{ marginBottom: '20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {/* Ligne 1 : search + filtres */}
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
              <input
                type="text"
                placeholder="🔍 Rechercher (nom, email, identifiant)..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                style={{ ...styles.input, maxWidth: '340px', flex: '1 1 220px' }}
              />
              {[
                { val: 'all',      label: `Tous (${accounts.length})` },
                { val: 'active',   label: `✅ Actifs (${activeCount})` },
                { val: 'expired',  label: `🔴 Expirés (${expiredCount})` },
                { val: 'trial',    label: `⚡ Test (${testCount})` },
                { val: 'disabled', label: `⛔ Désactivés` },
              ].map(f => (
                <button key={f.val} onClick={() => { setFilterStatus(f.val); setSelectedIds([]); }}
                  style={{ ...styles.quickBtn, ...(filterStatus === f.val ? styles.quickBtnActive : {}) }}>
                  {f.label}
                </button>
              ))}
            </div>
            {/* Ligne 2 : sélection en bloc */}
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', color: '#94A3B8', fontSize: '0.88rem' }}>
                <input
                  type="checkbox"
                  checked={filteredAccounts.length > 0 && selectedIds.length === filteredAccounts.length}
                  onChange={() => toggleSelectAll(filteredAccounts)}
                  style={{ width: '16px', height: '16px', accentColor: '#16A34A', cursor: 'pointer' }}
                />
                Tout sélectionner ({filteredAccounts.length})
              </label>
              {selectedIds.length > 0 && (
                <>
                  <span style={{ color: '#64748B', fontSize: '0.85rem' }}>
                    {selectedIds.length} sélectionné(s)
                  </span>
                  <button
                    onClick={handleBulkDelete}
                    style={{ padding: '6px 16px', background: '#EF444422', border: '1px solid #EF4444', borderRadius: '8px', color: '#F87171', cursor: 'pointer', fontWeight: '600', fontSize: '0.85rem' }}
                  >
                    🗑 Supprimer la sélection
                  </button>
                  <button
                    onClick={() => setSelectedIds([])}
                    style={{ padding: '6px 12px', background: 'transparent', border: '1px solid #2D3F50', borderRadius: '8px', color: '#94A3B8', cursor: 'pointer', fontSize: '0.82rem' }}
                  >
                    ✕ Désélectionner
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {/* Grille des comptes */}
        {loading ? (
          <div style={styles.emptyState}>⏳ Chargement...</div>
        ) : accounts.length === 0 ? (
          <div style={styles.emptyState}>Aucun compte Xtream généré</div>
        ) : filteredAccounts.length === 0 ? (
          <div style={styles.emptyState}>Aucun résultat pour « {searchTerm} »</div>
        ) : (
          <div style={styles.grid}>
            {filteredAccounts.map(acc => {
              const isExpired = acc.is_expired;
              const isDisabled = acc.is_enabled === 0;
              const isTrial = acc.is_trial === 1;
              const startsOnFirstConnection = Number(acc.trial_starts_on_first_connection) === 1 && !acc.trial_started_at;
              const statusColor = isExpired ? '#EF4444' : isDisabled ? '#6B7280' : '#22C55E';
              const statusLabel = startsOnFirstConnection ? 'En attente' : isExpired ? 'Expiré' : isDisabled ? 'Désactivé' : 'Actif';
              const shortM3u = acc.short_token ? `${XTREAM_URL}/m/${acc.short_token}` : `${XTREAM_URL}/get.php?username=${acc.username}&password=${acc.password}&type=m3u_plus&output=ts`;
              const creds = formatXtreamCredentials({
                username: acc.username,
                password: acc.password,
                m3uUrl: shortM3u,
                expiresAt: acc.expires_at
              });

              return (
                <div key={acc.id} style={{ ...styles.card, opacity: isExpired ? 0.75 : 1, outline: selectedIds.includes(acc.id) ? '2px solid #16A34A' : 'none' }}>

                  {/* Card Header */}
                  <div style={styles.cardHeader}>
                    <div style={styles.cardHeaderLeft}>
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(acc.id)}
                        onChange={() => toggleSelect(acc.id)}
                        style={{ width: '15px', height: '15px', accentColor: '#16A34A', cursor: 'pointer', flexShrink: 0 }}
                      />
                      <div style={{ ...styles.statusDot, background: statusColor }} />
                      <span style={{ ...styles.statusText, color: statusColor }}>{statusLabel}</span>
                      {isTrial && <span style={styles.trialBadge}>TEST</span>}
                      {startsOnFirstConnection && <span style={styles.waitingBadge}>1re connexion</span>}
                    </div>
                    <span style={styles.connCount}>
                      🔗 {acc.connection_count} / {acc.max_connections}
                    </span>
                  </div>

                  {/* Identité */}
                  <div style={styles.identity}>
                    <div style={styles.avatar}>{(acc.nom || acc.username || '?')[0].toUpperCase()}</div>
                    <div>
                      <div style={styles.userName}>{acc.nom || '—'}</div>
                      <div style={styles.userEmail}>{acc.reseller_name && /^demo_.*@fluxivo\.local$/i.test(acc.email || '') ? `Revendeur : ${acc.reseller_name}` : (acc.email || '—')}</div>
                    </div>
                  </div>

                  {/* Credentials */}
                  <div style={styles.credsBox}>
                    <div style={styles.credsRow}>
                      <span style={styles.credsKey}>Name</span>
                      <span style={styles.credsVal}>{XTREAM_APP_NAME}</span>
                    </div>
                    <div style={styles.credsRow}>
                      <span style={styles.credsKey}>Utilisateur</span>
                      <span style={styles.credsVal}>{acc.username}</span>
                    </div>
                    <div style={styles.credsRow}>
                      <span style={styles.credsKey}>Mot de passe</span>
                      <span style={styles.credsVal}>{acc.password}</span>
                    </div>
                    <div style={styles.credsRow}>
                      <span style={styles.credsKey}>URL Xtream</span>
                      <span style={{ ...styles.credsVal, fontSize: '0.7rem' }}>{XTREAM_URL}</span>
                    </div>
                    <div style={styles.credsRow}>
                      <span style={styles.credsKey}>M3U</span>
                      <span style={{ ...styles.credsVal, fontSize: '0.75rem', wordBreak: 'break-all', color: '#60A5FA' }}>
                        {shortM3u}
                      </span>
                    </div>
                    <button
                      style={{ ...styles.copyBtn, ...(copiedItem === acc.id ? styles.copyBtnDone : {}) }}
                      onClick={() => copyToClipboard(creds, acc.id)}
                    >
                      {copiedItem === acc.id ? '✅ Copié' : '📋 Copier tout'}
                    </button>
                  </div>

                  {/* Dates */}
                  <div style={styles.datesBox}>
                    <div style={styles.dateRow}>
                      <span style={styles.dateKey}>Créé</span>
                      <span style={styles.dateVal}>{new Date(acc.created_at).toLocaleDateString('fr-FR')}</span>
                    </div>
                    <div style={styles.dateRow}>
                      <span style={styles.dateKey}>Expire</span>
                      <span style={{ ...styles.dateVal, color: isExpired ? '#EF4444' : '#22C55E' }}>
                        {startsOnFirstConnection ? 'Après 1re connexion' : new Date(acc.expires_at).toLocaleDateString('fr-FR')}
                      </span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div style={styles.actions}>
                    <button
                      style={{ ...styles.actionBtn, background: isDisabled ? '#16A34A22' : '#6B728022', color: isDisabled ? '#22C55E' : '#9CA3AF' }}
                      onClick={() => handleToggle(acc.id)}
                    >
                      {isDisabled ? '▶ Activer' : '⏸ Pause'}
                    </button>
                    <button
                      style={{ ...styles.actionBtn, background: '#F59E0B22', color: '#FCD34D' }}
                      onClick={() => handleResetPassword(acc)}
                      title="Générer un nouveau mot de passe Xtream"
                    >
                      🔑 Nouveau MDP
                    </button>
                    <button
                      style={{ ...styles.actionBtn, background: '#3B82F622', color: '#60A5FA' }}
                      onClick={() => { setExtendModal(acc); setExtendDays(30); setExtendAmount(2000); setExtendMode('direct'); setGeneratedLink(''); }}
                    >
                      ⏳ Prolonger
                    </button>
                    <button
                      style={{ ...styles.actionBtn, background: '#8B5CF622', color: '#A78BFA' }}
                      onClick={() => openConnectionsModal(acc)}
                      title="Modifier la limite d'appareils"
                    >
                      📱 {acc.max_connections || 2} app.
                    </button>
                    {acc.whatsapp && acc.whatsapp.replace(/\D/g, '') !== '0000000000' && (
                      <button
                        style={{ ...styles.actionBtn, background: '#22C55E22', color: '#4ADE80' }}
                        onClick={() => triggerWhatsAppClient(acc)}
                        title="Relancer ce client par WhatsApp"
                      >
                        💬 WhatsApp
                      </button>
                    )}
                    {isTrial && (
                      <button
                        style={{ ...styles.actionBtn, background: '#F59E0B22', color: '#FCD34D' }}
                        onClick={() => { setConvertModal(acc); setConvertDays(30); }}
                      >
                        🔄 Convertir
                      </button>
                    )}
                    <button
                      style={{ ...styles.actionBtn, background: '#EF444422', color: '#F87171' }}
                      onClick={() => handleDelete(acc.id)}
                    >
                      🗑 Suppr.
                    </button>
                    <button
                      style={{ ...styles.actionBtn, background: '#F9731622', color: '#FB923C', flexBasis: '100%' }}
                      onClick={() => openBlockCatModal(acc)}
                    >
                      🚫 Catégories bloquées {blockedCats.length > 0 && blockCatModal?.id === acc.id ? `(${blockedCats.length})` : ''}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Modal Prolonger */}
        {extendModal && (
          <div style={styles.overlay} onClick={() => { setExtendModal(null); setGeneratedLink(''); }}>
            <div style={styles.modal} onClick={e => e.stopPropagation()}>
              <h3 style={styles.modalTitle}>⏳ Prolonger le compte</h3>
              <p style={styles.modalSubtitle}><strong>{extendModal.nom || extendModal.username}</strong></p>

              {/* Toggle mode */}
              <div style={styles.modeToggle}>
                <button
                  style={{ ...styles.modeBtn, ...(extendMode === 'direct' ? styles.modeBtnActive : {}) }}
                  onClick={() => { setExtendMode('direct'); setGeneratedLink(''); }}
                >✅ Prolonger directement</button>
                <button
                  style={{ ...styles.modeBtn, ...(extendMode === 'link' ? styles.modeBtnActiveOrange : {}) }}
                  onClick={() => { setExtendMode('link'); setGeneratedLink(''); }}
                >🔗 Générer lien de paiement</button>
              </div>

              {/* Durée */}
              <div style={styles.presetGrid}>
                {EXTEND_PRESETS.map(p => (
                  <button key={p.days}
                    style={{ ...styles.presetBtn, ...(extendDays === p.days ? styles.presetBtnActive : {}) }}
                    onClick={() => setExtendDays(p.days)}
                  >{p.label}</button>
                ))}
              </div>
              {extendMode === 'direct' && (
                <div style={styles.presetGrid}>
                  {EXTEND_PRESETS.slice(0, 3).map(p => (
                    <button key={`minus-${p.days}`}
                      style={{ ...styles.presetBtn, ...(extendDays === -p.days ? styles.presetBtnDanger : {}) }}
                      onClick={() => setExtendDays(-p.days)}
                    >-{p.label}</button>
                  ))}
                </div>
              )}
              <div style={styles.customRow}>
                <input type="number" min={extendMode === 'direct' ? '-3650' : '1'} style={styles.input} value={extendDays}
                  onChange={e => {
                    const next = parseInt(e.target.value, 10);
                    if (!Number.isFinite(next)) return setExtendDays(extendMode === 'direct' ? 0 : 1);
                    setExtendDays(extendMode === 'link' && next < 1 ? 1 : next);
                  }} />
                <span style={{ color: '#94A3B8' }}>jours</span>
              </div>
              {extendMode === 'direct' && extendDays < 0 && (
                <div style={styles.warningBox}>
                  Cette action va soustraire {Math.abs(extendDays)} jour(s) à la date d'expiration.
                </div>
              )}

              {/* Champ montant — visible seulement en mode lien */}
              {extendMode === 'link' && (
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ color: '#94A3B8', fontSize: '0.8rem', display: 'block', marginBottom: '6px' }}>Montant à facturer (FCFA)</label>
                  <input type="number" min="1" style={styles.input} value={extendAmount}
                    onChange={e => setExtendAmount(parseInt(e.target.value) || 1)}
                    placeholder="Ex: 2000" />
                </div>
              )}

              {/* Lien généré */}
              {generatedLink && (
                <div style={styles.linkBox}>
                  <div style={{ color: '#22C55E', fontSize: '0.78rem', marginBottom: '6px', fontWeight: '600' }}>✅ Lien généré — envoyez-le au client :</div>
                  <div style={styles.linkText}>{generatedLink}</div>
                  <button style={styles.copyLinkBtn} onClick={() => { navigator.clipboard.writeText(generatedLink); }}>
                    📋 Copier le lien
                  </button>
                </div>
              )}

              <div style={styles.modalActions}>
                {extendMode === 'direct' ? (
                  <button style={styles.confirmBtn} onClick={handleExtend} disabled={actionLoading}>
                    {actionLoading ? '⏳...' : extendDays < 0 ? `➖ Soustraire ${Math.abs(extendDays)} jours` : `✅ Prolonger de ${extendDays} jours`}
                  </button>
                ) : (
                  <button style={{ ...styles.confirmBtn, background: 'linear-gradient(135deg, #F59E0B, #D97706)' }}
                    onClick={handleGenerateLink} disabled={actionLoading}>
                    {actionLoading ? '⏳...' : `🔗 Générer le lien — ${extendAmount.toLocaleString()} FCFA`}
                  </button>
                )}
                <button style={styles.cancelBtn} onClick={() => { setExtendModal(null); setGeneratedLink(''); }}>Fermer</button>
              </div>
            </div>
          </div>
        )}

        {/* Modal Limite Appareils */}
        {editConnModal && (
          <div style={styles.overlay} onClick={() => setEditConnModal(null)}>
            <div style={{ ...styles.modal, maxWidth: '520px' }} onClick={e => e.stopPropagation()}>
              <h3 style={styles.modalTitle}>📱 Appareils autorisés</h3>
              <p style={styles.modalSubtitle}><strong>{editConnModal.nom || editConnModal.username}</strong></p>
              <p style={{ color: '#94A3B8', fontSize: '0.85rem', marginBottom: '20px' }}>
                Nombre maximum d'appareils pouvant utiliser durablement ce compte Xtream.
              </p>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ color: '#94A3B8', fontSize: '0.85rem', display: 'block', marginBottom: '8px' }}>
                  Nombre d'appareils (1 – 10)
                </label>
                <input
                  type="number" min="1" max="10" value={editConnValue}
                  onChange={e => setEditConnValue(Math.max(1, Math.min(10, parseInt(e.target.value) || 1)))}
                  style={{ ...styles.input, width: '100%', textAlign: 'center', fontSize: '1.4rem', fontWeight: 'bold' }}
                />
              </div>

              <div style={styles.devicesPanel}>
                <div style={styles.devicesHeader}>
                  <div>
                    <div style={styles.devicesTitle}>Liste autorisée</div>
                    <div style={styles.devicesCount}>{activeDevices.length} / {editConnValue} appareil(s)</div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    <button
                      style={{ ...styles.refreshBtn, borderColor: '#EF444455', color: '#F87171' }}
                      onClick={handleClearAccountConnections}
                      disabled={clearingAccountConnections || devicesLoading}
                      title="Supprime les connexions actives de ce compte sans retirer les appareils autorisés."
                    >
                      {clearingAccountConnections ? '...' : 'Vider connexions'}
                    </button>
                    <button
                      style={styles.refreshBtn}
                      onClick={() => loadActiveDevices(editConnModal)}
                      disabled={devicesLoading}
                    >
                      {devicesLoading ? '⏳' : '↻'} Actualiser
                    </button>
                  </div>
                </div>

                {devicesLoading && activeDevices.length === 0 ? (
                  <div style={styles.devicesEmpty}>Chargement des appareils...</div>
                ) : activeDevices.length === 0 ? (
                  <div style={styles.devicesEmpty}>Aucun appareil autorisé. Le premier appareil qui se connectera prendra une place.</div>
                ) : (
                  <div style={styles.devicesList}>
                    {activeDevices.map(device => (
                      <div key={device.id || device.ip_address} style={styles.deviceRow}>
                        <div style={styles.deviceInfo}>
                          <div style={styles.deviceIp}>{device.device_id || device.ip_address}</div>
                          <div style={styles.deviceMeta}>
                            {device.device_id ? `${device.identity_source || 'Device ID'} • IP ${device.ip_address || '-'} • ` : ''}
                            {device.is_active ? `${device.active_streams || 0} flux actif(s)` : 'Pas de flux actif'}
                            {device.connection_types?.length ? ` • ${device.connection_types.join(', ')}` : ''}
                          </div>
                          {device.user_agent && (
                            <div style={styles.deviceAgent} title={device.user_agent}>{device.user_agent}</div>
                          )}
                        </div>
                        <div style={styles.deviceActions}>
                          {device.is_active && device.ip_address && (
                            <button
                              style={{ ...styles.deviceRemoveBtn, background: '#F59E0B22', borderColor: '#F59E0B55', color: '#FCD34D' }}
                              onClick={() => handleDisconnectDevice(device)}
                              disabled={removingDeviceIp === `disconnect:${device.ip_address}`}
                              title="Déconnecte la session active sans retirer l'appareil autorisé."
                            >
                              {removingDeviceIp === `disconnect:${device.ip_address}` ? '...' : 'Déconnecter'}
                            </button>
                          )}
                          <button
                            style={styles.deviceRemoveBtn}
                            onClick={() => handleRemoveActiveDevice(device)}
                            disabled={removingDeviceIp === `remove:${device.id || device.ip_address}`}
                            title="Retire l'appareil de la liste autorisée."
                          >
                            {removingDeviceIp === `remove:${device.id || device.ip_address}` ? '...' : 'Supprimer'}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {observedDevices.length > 0 && (
                  <div style={{ borderTop: '1px solid #1E2D3D', marginTop: '12px', paddingTop: '12px' }}>
                    <div style={styles.devicesTitle}>IP récemment observées</div>
                    <div style={{ ...styles.devicesCount, marginBottom: '8px' }}>
                      Historique visible, mais ces IP ne prennent pas de place autorisée.
                    </div>
                    <div style={styles.devicesList}>
                      {observedDevices.map(device => (
                        <div key={`observed-${device.id}-${device.ip_address}`} style={{ ...styles.deviceRow, opacity: 0.78 }}>
                          <div style={styles.deviceInfo}>
                            <div style={styles.deviceIp}>{device.ip_address}</div>
                            <div style={styles.deviceMeta}>
                              Non autorisée
                              {device.is_active ? ' • activité récente' : ''}
                              {device.last_activity ? ` • vue ${new Date(device.last_activity).toLocaleString('fr-FR')}` : ''}
                            </div>
                            {device.user_agent && (
                              <div style={styles.deviceAgent} title={device.user_agent}>{device.user_agent}</div>
                            )}
                          </div>
                          <button
                            style={styles.deviceRemoveBtn}
                            onClick={() => handleRemoveObservedDevice(device)}
                            disabled={removingDeviceIp === `observed:${device.ip_address}`}
                            title="Supprime cette IP de l'historique observé."
                          >
                            {removingDeviceIp === `observed:${device.ip_address}` ? '...' : 'Supprimer'}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button style={{ ...styles.btnPrimary, flex: 1 }} onClick={handleSaveConnections}>✅ Enregistrer</button>
                <button style={{ ...styles.btnCancel, flex: 1 }} onClick={() => setEditConnModal(null)}>Annuler</button>
              </div>
            </div>
          </div>
        )}

        {/* Modal Convertir */}
        {convertModal && (
          <div style={styles.overlay} onClick={() => setConvertModal(null)}>
            <div style={styles.modal} onClick={e => e.stopPropagation()}>
              <h3 style={styles.modalTitle}>🔄 Convertir en abonnement</h3>
              <p style={styles.modalSubtitle}><strong>{convertModal.nom || convertModal.username}</strong></p>
              <p style={{ color: '#94A3B8', fontSize: '0.85rem', marginBottom: '16px' }}>
                Le compte test sera converti en abonnement actif pour la durée choisie.
              </p>
              <div style={styles.presetGrid}>
                {CONVERT_PRESETS.map(p => (
                  <button key={p.days}
                    style={{ ...styles.presetBtn, ...(convertDays === p.days ? styles.presetBtnActive : {}) }}
                    onClick={() => setConvertDays(p.days)}
                  >{p.label}</button>
                ))}
              </div>
              <div style={styles.customRow}>
                <input type="number" min="1" style={styles.input} value={convertDays}
                  onChange={e => setConvertDays(parseInt(e.target.value) || 1)} />
                <span style={{ color: '#94A3B8' }}>jours</span>
              </div>
              <div style={styles.modalActions}>
                <button style={{ ...styles.confirmBtn, background: 'linear-gradient(135deg, #F59E0B, #D97706)' }}
                  onClick={handleConvert} disabled={actionLoading}>
                  {actionLoading ? '⏳...' : `🔄 Convertir pour ${convertDays} jours`}
                </button>
                <button style={styles.cancelBtn} onClick={() => setConvertModal(null)}>Annuler</button>
              </div>
            </div>
          </div>
        )}
        {/* Modal Catégories bloquées */}
        {blockCatModal && (
          <div style={styles.overlay} onClick={() => setBlockCatModal(null)}>
            <div style={{ ...styles.modal, maxWidth: '520px', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
              <h3 style={styles.modalTitle}>🚫 Catégories bloquées</h3>
              <p style={styles.modalSubtitle}>
                <strong>{blockCatModal.nom || blockCatModal.username}</strong> — {blockedCats.length} catégorie(s) bloquée(s)
              </p>
              <input
                type="text"
                placeholder="🔍 Rechercher une catégorie..."
                value={catSearch}
                onChange={e => setCatSearch(e.target.value)}
                style={{ ...styles.input, marginBottom: '8px', flexShrink: 0 }}
              />
              {!blockLoading && (() => {
                const filtered = allCategories.filter(cat => !catSearch || cat.category_name.toLowerCase().includes(catSearch.toLowerCase()));
                const allBlocked = filtered.length > 0 && filtered.every(c => blockedCats.includes(formatCategoryKey(c)));
                const noneBlocked = filtered.every(c => !blockedCats.includes(formatCategoryKey(c)));
                return (
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '10px', flexShrink: 0 }}>
                    <button
                      style={{ flex: 1, padding: '7px', background: allBlocked ? '#EF444422' : '#EF444415', border: '1px solid #EF444455', borderRadius: '8px', color: '#F87171', cursor: 'pointer', fontSize: '0.78rem', fontWeight: '600' }}
                      onClick={() => handleBlockAll(filtered)}
                      disabled={allBlocked}
                    >
                      🚫 Tout bloquer ({filtered.length})
                    </button>
                    <button
                      style={{ flex: 1, padding: '7px', background: noneBlocked ? '#16A34A22' : '#16A34A15', border: '1px solid #16A34A55', borderRadius: '8px', color: '#22C55E', cursor: 'pointer', fontSize: '0.78rem', fontWeight: '600' }}
                      onClick={() => handleUnblockAll(filtered)}
                      disabled={noneBlocked}
                    >
                      ✅ Tout débloquer
                    </button>
                  </div>
                );
              })()}
              {blockLoading ? (
                <p style={{ color: '#94A3B8', textAlign: 'center', padding: '20px' }}>⏳ Chargement...</p>
              ) : (
                <div style={{ overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: '6px', paddingRight: '4px' }}>
                  {allCategories
                    .filter(cat => !catSearch || cat.category_name.toLowerCase().includes(catSearch.toLowerCase()))
                    .map(cat => {
                      const catKey = formatCategoryKey(cat);
                      const isBlocked = blockedCats.includes(catKey);
                      return (
                        <div key={catKey} style={{
                          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          padding: '10px 12px', borderRadius: '8px', cursor: 'pointer',
                          background: isBlocked ? '#EF444415' : '#0D1117',
                          border: `1px solid ${isBlocked ? '#EF444455' : '#1E2D3D'}`,
                          transition: 'all 0.15s'
                        }} onClick={() => toggleBlockCategory(blockCatModal, cat)}>
                          <div>
                            <span style={{ color: isBlocked ? '#F87171' : '#E2E8F0', fontSize: '0.85rem', fontWeight: '500' }}>
                              {isBlocked ? '🚫 ' : '✅ '}{getCategoryLabel(cat)}
                            </span>
                            <span style={{ color: '#475569', fontSize: '0.72rem', marginLeft: '8px' }}>{cat.count} éléments</span>
                          </div>
                          <span style={{
                            fontSize: '0.7rem', fontWeight: '700', padding: '3px 10px', borderRadius: '20px',
                            background: isBlocked ? '#EF444422' : '#16A34A22',
                            color: isBlocked ? '#F87171' : '#22C55E',
                            border: `1px solid ${isBlocked ? '#EF444455' : '#16A34A55'}`
                          }}>
                            {isBlocked ? 'BLOQUÉE' : 'ACTIVE'}
                          </span>
                        </div>
                      );
                    })}
                </div>
              )}
              <button style={{ ...styles.cancelBtn, marginTop: '16px', flexShrink: 0 }} onClick={() => setBlockCatModal(null)}>
                Fermer
              </button>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

const styles = {
  page: { padding: '24px', maxWidth: '1400px', margin: '0 auto' },
  pageHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '28px' },
  pageTitle: { fontSize: '1.8rem', fontWeight: '700', color: '#F1F5F9', margin: 0 },
  pageSubtitle: { color: '#64748B', marginTop: '4px', fontSize: '0.9rem' },
  backBtn: { padding: '8px 16px', background: '#1E2D3D', border: '1px solid #2D3F50', borderRadius: '8px', color: '#94A3B8', cursor: 'pointer', fontSize: '0.85rem' },

  statsRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '12px', marginBottom: '28px' },
  statCard: { background: '#111827', borderRadius: '12px', padding: '16px 10px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', border: '1px solid #1E2D3D', minWidth: 0 },
  statIcon: { fontSize: '1.4rem' },
  statValue: { fontSize: '1.6rem', fontWeight: '700', lineHeight: 1 },
  statLabel: { color: '#64748B', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'center' },

  generateCard: { background: '#111827', border: '1px solid #1E2D3D', borderRadius: '14px', padding: '24px', marginBottom: '28px' },
  cardTitle: { color: '#F1F5F9', fontWeight: '600', marginBottom: '16px', fontSize: '1rem' },
  generateForm: { display: 'flex', flexDirection: 'column', gap: '14px' },
  input: { background: '#0D1117', border: '1px solid #2D3F50', borderRadius: '8px', padding: '10px 14px', color: '#F1F5F9', fontSize: '0.9rem', outline: 'none', width: '100%', boxSizing: 'border-box' },
  fieldLabel: { color: '#94A3B8', fontSize: '0.78rem', display: 'block', marginBottom: '6px', fontWeight: '600' },
  btnPrimary: { padding: '10px 20px', background: 'linear-gradient(135deg, #16A34A, #22C55E)', border: 'none', borderRadius: '8px', color: '#fff', fontWeight: '600', cursor: 'pointer', fontSize: '0.9rem' },
  btnCancel: { padding: '10px 20px', background: '#1A2436', border: '1px solid #2D3F50', borderRadius: '8px', color: '#94A3B8', fontWeight: '600', cursor: 'pointer', fontSize: '0.9rem' },
  quickBtns: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
  quickBtn: { padding: '6px 14px', background: '#1A2436', border: '1px solid #2D3F50', borderRadius: '20px', color: '#94A3B8', cursor: 'pointer', fontSize: '0.8rem', transition: 'all 0.2s' },
  quickBtnActive: { background: '#16A34A22', border: '1px solid #16A34A', color: '#22C55E' },
  customDuration: { display: 'flex', alignItems: 'center', gap: '10px' },
  select: { background: '#0D1117', border: '1px solid #2D3F50', borderRadius: '8px', padding: '10px 12px', color: '#F1F5F9', fontSize: '0.9rem' },
  durationLabel: { color: '#94A3B8', fontSize: '0.9rem' },
  generateBtn: { padding: '12px 24px', background: 'linear-gradient(135deg, #16A34A, #15803D)', border: 'none', borderRadius: '10px', color: 'white', fontWeight: '600', cursor: 'pointer', fontSize: '0.95rem', alignSelf: 'flex-start' },
  bulkRow: { display: 'flex', gap: '14px', alignItems: 'flex-end', flexWrap: 'wrap' },
  bulkResultBox: { background: '#0D1117', border: '1px solid #16A34A55', borderRadius: '12px', padding: '14px', marginTop: '4px' },
  bulkResultHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginBottom: '12px', flexWrap: 'wrap' },
  bulkResultTitle: { color: '#22C55E', fontSize: '0.88rem', fontWeight: '700' },
  bulkList: { display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '360px', overflowY: 'auto', paddingRight: '4px' },
  bulkItem: { display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'flex-start', background: '#111827', border: '1px solid #1E2D3D', borderRadius: '10px', padding: '10px 12px' },
  bulkCreds: { color: '#CBD5E1', fontSize: '0.78rem', lineHeight: 1.55, fontFamily: 'monospace', wordBreak: 'break-word' },
  bulkLink: { color: '#60A5FA', wordBreak: 'break-all' },
  bulkMore: { color: '#94A3B8', fontSize: '0.8rem', textAlign: 'center', padding: '8px' },

  errorBanner: { background: '#EF444420', border: '1px solid #EF4444', borderRadius: '10px', padding: '12px 16px', color: '#F87171', marginBottom: '20px' },
  emptyState: { textAlign: 'center', color: '#64748B', padding: '60px 20px', fontSize: '1rem' },

  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '20px' },
  card: { background: '#111827', border: '1px solid #1E2D3D', borderRadius: '16px', padding: '20px', display: 'flex', flexDirection: 'column', gap: '14px', transition: 'border-color 0.2s', boxShadow: '0 4px 20px rgba(0,0,0,0.3)' },

  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  cardHeaderLeft: { display: 'flex', alignItems: 'center', gap: '8px' },
  statusDot: { width: '8px', height: '8px', borderRadius: '50%' },
  statusText: { fontSize: '0.8rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' },
  trialBadge: { background: '#F59E0B22', border: '1px solid #F59E0B55', color: '#FCD34D', fontSize: '0.65rem', padding: '2px 8px', borderRadius: '20px', fontWeight: '700', letterSpacing: '0.08em' },
  waitingBadge: { background: '#3B82F622', border: '1px solid #3B82F655', color: '#93C5FD', fontSize: '0.65rem', padding: '2px 8px', borderRadius: '20px', fontWeight: '700' },
  connCount: { color: '#64748B', fontSize: '0.78rem' },

  identity: { display: 'flex', alignItems: 'center', gap: '12px', paddingBottom: '14px', borderBottom: '1px solid #1E2D3D' },
  avatar: { width: '40px', height: '40px', borderRadius: '10px', background: 'linear-gradient(135deg, #16A34A, #3B82F6)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: '700', fontSize: '1.1rem', flexShrink: 0 },
  userName: { color: '#F1F5F9', fontWeight: '600', fontSize: '0.95rem' },
  userEmail: { color: '#64748B', fontSize: '0.78rem', marginTop: '2px' },

  credsBox: { background: '#0D1117', border: '1px solid #1E2D3D', borderRadius: '10px', padding: '12px', display: 'flex', flexDirection: 'column', gap: '6px' },
  credsRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  credsKey: { color: '#64748B', fontSize: '0.75rem', minWidth: '90px' },
  credsVal: { color: '#E2E8F0', fontSize: '0.8rem', fontFamily: 'monospace', wordBreak: 'break-all', textAlign: 'right' },
  copyBtn: { marginTop: '8px', padding: '6px 12px', background: '#1E2D3D', border: '1px solid #2D3F50', borderRadius: '6px', color: '#94A3B8', cursor: 'pointer', fontSize: '0.78rem', alignSelf: 'flex-end', transition: 'all 0.2s' },
  copyBtnDone: { background: '#16A34A22', border: '1px solid #16A34A', color: '#22C55E' },

  datesBox: { display: 'flex', justifyContent: 'space-between' },
  dateRow: { display: 'flex', flexDirection: 'column', gap: '2px' },
  dateKey: { color: '#64748B', fontSize: '0.72rem', textTransform: 'uppercase' },
  dateVal: { color: '#CBD5E1', fontSize: '0.82rem', fontWeight: '500' },

  actions: { display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '4px' },
  actionBtn: { flex: 1, minWidth: '70px', padding: '7px 10px', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: '600', transition: 'opacity 0.2s' },

  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(4px)' },
  modal: { background: '#111827', border: '1px solid #1E2D3D', borderRadius: '16px', padding: '28px', width: '100%', maxWidth: '420px', boxShadow: '0 20px 60px rgba(0,0,0,0.6)' },
  modalTitle: { color: '#F1F5F9', fontSize: '1.1rem', fontWeight: '700', marginBottom: '4px' },
  modalSubtitle: { color: '#94A3B8', fontSize: '0.9rem', marginBottom: '20px' },
  presetGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px', marginBottom: '16px' },
  presetBtn: { padding: '8px', background: '#1A2436', border: '1px solid #2D3F50', borderRadius: '8px', color: '#94A3B8', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '500' },
  presetBtnActive: { background: '#16A34A22', border: '1px solid #16A34A', color: '#22C55E' },
  presetBtnDanger: { background: '#EF444422', border: '1px solid #EF4444', color: '#F87171' },
  customRow: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' },
  warningBox: { background: '#F59E0B18', border: '1px solid #F59E0B55', color: '#FCD34D', borderRadius: '8px', padding: '10px 12px', fontSize: '0.8rem', marginBottom: '14px' },
  modalActions: { display: 'flex', flexDirection: 'column', gap: '10px' },
  confirmBtn: { padding: '12px', background: 'linear-gradient(135deg, #16A34A, #15803D)', border: 'none', borderRadius: '10px', color: 'white', fontWeight: '700', cursor: 'pointer', fontSize: '0.9rem' },
  cancelBtn: { padding: '10px', background: 'transparent', border: '1px solid #2D3F50', borderRadius: '10px', color: '#64748B', cursor: 'pointer', fontSize: '0.85rem' },

  devicesPanel: { background: '#0D1117', border: '1px solid #1E2D3D', borderRadius: '10px', padding: '12px', marginBottom: '18px' },
  devicesHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginBottom: '10px' },
  devicesTitle: { color: '#E2E8F0', fontSize: '0.88rem', fontWeight: '700' },
  devicesCount: { color: '#64748B', fontSize: '0.74rem', marginTop: '2px' },
  refreshBtn: { padding: '7px 10px', background: '#1A2436', border: '1px solid #2D3F50', borderRadius: '8px', color: '#94A3B8', cursor: 'pointer', fontSize: '0.78rem', flexShrink: 0 },
  devicesEmpty: { color: '#64748B', fontSize: '0.82rem', textAlign: 'center', padding: '14px 8px' },
  devicesList: { display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '240px', overflowY: 'auto' },
  deviceRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', background: '#111827', border: '1px solid #1E2D3D', borderRadius: '8px', padding: '10px' },
  deviceInfo: { minWidth: 0, flex: 1 },
  deviceIp: { color: '#F1F5F9', fontSize: '0.88rem', fontWeight: '700', fontFamily: 'monospace' },
  deviceMeta: { color: '#94A3B8', fontSize: '0.75rem', marginTop: '3px' },
  deviceAgent: { color: '#64748B', fontSize: '0.72rem', marginTop: '3px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  deviceActions: { display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'stretch', flexShrink: 0 },
  deviceRemoveBtn: { padding: '7px 10px', background: '#EF444422', border: '1px solid #EF444455', borderRadius: '8px', color: '#F87171', cursor: 'pointer', fontSize: '0.76rem', fontWeight: '700', flexShrink: 0 },

  modeToggle: { display: 'flex', gap: '8px', marginBottom: '16px' },
  modeBtn: { flex: 1, padding: '8px', background: '#1A2436', border: '1px solid #2D3F50', borderRadius: '8px', color: '#64748B', cursor: 'pointer', fontSize: '0.78rem', fontWeight: '500' },
  modeBtnActive: { background: '#16A34A22', border: '1px solid #16A34A', color: '#22C55E' },
  modeBtnActiveOrange: { background: '#F59E0B22', border: '1px solid #F59E0B', color: '#FCD34D' },

  linkBox: { background: '#0D1117', border: '1px solid #16A34A55', borderRadius: '10px', padding: '14px', marginBottom: '16px' },
  linkText: { color: '#22C55E', fontSize: '0.75rem', wordBreak: 'break-all', fontFamily: 'monospace', marginBottom: '10px', lineHeight: 1.5 },
  copyLinkBtn: { padding: '6px 14px', background: '#16A34A22', border: '1px solid #16A34A', borderRadius: '6px', color: '#22C55E', cursor: 'pointer', fontSize: '0.78rem', fontWeight: '600' },
};

export default AdminXtream;
